import { Box, Checkbox, Stack } from "@mui/material";
import { ForgotPasswordViewModel } from "./ForgotPasswordViewModel";
import { CustomTextView } from "../../../component/CustomTextView";
import { useTranslation } from "react-i18next";
import { CustomCardView } from "../../../component/CustomCardView";
import { CustomTextField } from "../../../component/CustomTextField";
import { CustomButtonView } from "../../../component/CustomButtonView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { COLOR_GREEN, COLOR_LIGHT_GRAY, COLOR_MAIN } from "../../../../data/colors/Colors";
import { CheckCircleRounded, EmailRounded, LockRounded } from "@mui/icons-material";
import { useState } from "react";
import { Validation } from "../../../../domain/validation/Validation";
import AutoHeight from "../../../component/AutoHeightView";
import { CustomImageView } from "../../../component/CustomImageView";
import { APP_ICON_IMAGE_WITHOUT_TITLE } from "../../../../data/constant/Constants";
import { provideForgotPasswordUseCase } from "../../../di/UseCasesModule";

export function ForgotPasswordView() {
  const viewModel = ForgotPasswordViewModel(provideForgotPasswordUseCase());

  const { t } = useTranslation();

  const { width } = useWindowDimensions();
  const isMobile = width < 900;

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors = validateInputs();
    setErrors(errors);

    if (Object.keys(errors).length === 0) viewModel.sendResetPasswordEmail();
  };

  const validateInputs = () => {
    const errors: { [key: string]: string } = {};
    const emailValid = Validation.validateEmail(viewModel.email);
    if (emailValid !== true) {
      errors.email = t("email_not_valid");
    }
    if (viewModel.email.length === 0) {
      errors.email = t("please_enter_field", { field: t("email") });
    }
    return errors;
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        position: "relative",
        bgcolor: COLOR_MAIN,
      }}
    >
      <CustomImageView
        width={"100%"}
        height={"100%"}
        src={APP_ICON_IMAGE_WITHOUT_TITLE}
        scale={isMobile ? "cover" : "contain"}
        props={{ position: "absolute" }}
      />
      <Box
        noValidate
        component={"form"}
        onSubmit={handleSubmit}
        width={"100%"}
        sx={{ zIndex: 10, position: "relative" }}
      >
        <Stack
          width={"100vw"}
          height={"100vh"}
          alignItems={"center"}
          p={3}
          justifyContent={"center"}
        >
          <CustomCardView
            minHeight={"40vh"}
            width={isMobile ? "100%" : "50vw"}
            children={
              <Stack
                width={"100%"}
                height={"100%"}
                alignItems={"center"}
                gap={4}
                justifyContent={
                  viewModel.isResetPasswordEmailSent
                    ? "center"
                    : "space-between"
                }
              >
                {viewModel.isResetPasswordEmailSent && (
                  <Stack gap={1} width={"100%"} alignItems={"center"}>
                    <CheckCircleRounded
                      sx={{ color: COLOR_GREEN, width: 80, height: 80 }}
                    />
                    <CustomTextView
                      text={t("email_sent")}
                      variant="h6"
                      textColor={"green"}
                    />
                    <CustomTextView
                      text={t("please_check_your_email_inbox")}
                      variant="subtitle1"
                      textColor={COLOR_LIGHT_GRAY}
                    />
                  </Stack>
                )}
                {!viewModel.isResetPasswordEmailSent && (
                  <>
                    <CustomTextView
                      text={t("forgot_password")}
                      variant="h4"
                      fontWeight="bold"
                      textColor={COLOR_MAIN}
                    />
                    <Stack width={"100%"}>
                      <AutoHeight>
                        <CustomTextField
                          type="email"
                          required
                          onTextChange={(email) => {
                            viewModel.setEmail(email);
                          }}
                          value={viewModel.email}
                          placeholder={t("email")}
                          startIcon={<EmailRounded />}
                          error={!!errors.email}
                          helperText={errors.email}
                          sxProps={{ p: 1 }}
                        />
                      </AutoHeight>
                    </Stack>

                    <Stack width={"100%"} pl={1} pr={1}>
                      <CustomButtonView
                        text={t("reset_password")}
                        props={{ pt: 1, pb: 1 }}
                        type="submit"
                      />
                    </Stack>
                  </>
                )}
              </Stack>
            }
          />
        </Stack>
      </Box>
    </Box>
  );
}
